import {
  FeatureFlag,
  getAllABVariationFlags,
} from "~/common/Featbit/getABFeatureFlag";
import Store from "~/common/Store";
import { getExclusiveTestIfEligible } from "./exclusiveTestHelpers";
import {
  getExpiredTestsFromCookie,
  updateStorageForExpiredTests,
} from "./expiredTestHelpers";
import {
  parseActiveFeatBitTests,
  updateStorageForNewTests,
} from "./activeTestHelpers";
import {
  getCurrentVariationIdFromCookie,
  getEligibleABTestLimit,
  handleChangedVariationIdCookie,
  updateEligibleTestLimit,
} from "./testTrackingHelpers";
import Cookie from "js-cookie";

export const ABTestLimit = 6;

const fallBack: FeatureFlag = {
  name: "abvariation-e-ee-ErrorFallback",
  stringValue: "e",
  kind: "RuleMatch",
  boolValue: "",
};

export interface DecodedTestInformation {
  exclusive: boolean;
  fullTestId: string;
  variant: string;
  fullVariationId: string;
  kind?: string;
  name: string;
}

let hasRun = false;
// Workaround for test purposes. Could also be used if ever needed to reset and rerun the actual retrieval of FeatBit AB Flags.
export function setHasRun(value: boolean) {
  hasRun = value;
}

export async function getAndUpdateVariationIds(): Promise<void> {
  if (!hasRun) {
    hasRun = true;
    await updateAllVariationsIds();
  }
}

/**
 * Retrieves the FeatureFlag value for the specified name.
 *
 * Will call check for updating variation ID if it is the first time called for the page.
 * Otherwise will return the value saved in session storage cache from the initial call on page load.
 *
 * Returns the default value if FeatBit fails to respond for any reason
 * @param key - The key of the feature flag to retrieve.
 * @param defaultValue - The default value to use in case the request to FeatBit errors.
 *
 * @example
 * ```jsx
 *  useEffect(() => {
 *   const getFlag = async () => {
 *      const flag = await getABFeatureFlagAndUpdateVariationId(
 *       "abvariation-2-02-SearchTest2",
 *      "a",
 *   );
 *      setFlag(flag);
 *   };
 *    getFlag();
 *  }, []);
 * ```
 */
export async function getABFeatureFlagAndUpdateVariationId(
  key: string,
  defaultValue: string,
): Promise<string> {
  if (!hasRun) {
    await getAndUpdateVariationIds();
    hasRun = true;
  }
  const value = sessionStorage.getItem(key);
  return value ?? defaultValue;
}

async function getActiveAbFeatBitFlags(
  sessionId: string,
  emailAddress: string,
): Promise<FeatureFlag[]> {
  const result = await getAllABVariationFlags(
    {
      customProps: [{ key: "Page", value: window.location.pathname }],
    },
    { sessionId, emailAddress },
  );
  if (result.isError) {
    // If there is an error getting the FeatBit flags, then the variationId should not be updated.
    return [fallBack];
  }
  return result.value;
}

function buildVariationIdFromActiveABTests(
  activeVariationTests: DecodedTestInformation[],
): string {
  const enrolledTests: string[] = [];
  activeVariationTests.forEach((variation) => {
    enrolledTests.push(variation.fullVariationId);
  });
  return enrolledTests.join(",");
}

function getDecodedTestInformation(
  flag: FeatureFlag,
): DecodedTestInformation | undefined {
  const split = flag.name.split("-");
  const departmentId = split[1];
  const testId = split[2];
  const exclusive = split[4];
  const variant = flag.stringValue;
  let fullVariationId = "";

  if (!departmentId || !testId) return;

  const fullTestId = departmentId + testId;
  fullVariationId += `${departmentId}${testId}:${variant}`;

  if (exclusive === "exclusive") {
    fullVariationId += ":exclusive";
  }
  return {
    fullTestId,
    fullVariationId,
    exclusive: exclusive === "exclusive",
    kind: flag.kind,
    variant,
    name: flag.name,
  };
}

function getSavedFeatBitTestInformation(
  variationId: string,
  activeCloudFlareTests: DecodedTestInformation[],
  activeFeatBitTests: FeatureFlag[],
): DecodedTestInformation[] {
  if (!variationId) return [];

  // Create a kvp of featbit test IDs to their names for easy lookup
  const featBitTestById = new Map(
    activeFeatBitTests.map((test) => {
      const featBitTestId = test.name.split("-").reduce((acc, currentValue) => {
        if (Number(currentValue)) return acc + currentValue;
        return acc;
      }, "");
      return [featBitTestId, test.name];
    }),
  );

  const tests = variationId.split(",");
  const decodedTests: DecodedTestInformation[] = [];
  tests.forEach((test) => {
    const parts = test.split(":");
    const fullTestId = parts[0];
    if (
      activeCloudFlareTests.find((cfTest) => cfTest.fullTestId === fullTestId)
    ) {
      // Expiring and enrolling Cloudflare tests are handled by the worker itself
      return;
    }
    const value = parts[1];
    const exclusive = parts[2];

    if (fullTestId && value) {
      decodedTests.push({
        fullTestId,
        exclusive: exclusive === "exclusive",
        fullVariationId: test,
        variant: value,
        name: featBitTestById.get(fullTestId) || fallBack.name,
      });
    }
  });
  return decodedTests;
}

function getCloudflareABTestCookies(): DecodedTestInformation[] {
  const cookies = Cookie.get();
  const abTestCookies = Object.keys(cookies).filter((cookie) =>
    cookie.startsWith("abvariation-"),
  );
  const abTestCookieObject: DecodedTestInformation[] = [];

  abTestCookies.forEach((cookie) => {
    const value = cookies[cookie];
    if (!value) return;
    const decoded = getDecodedTestInformation({
      name: cookie,
      stringValue: value,
      kind: "Cloudflare",
      boolValue: "",
    });

    if (decoded) {
      abTestCookieObject.push(decoded);
    }
  });
  return abTestCookieObject;
}

async function updateAllVariationsIds(): Promise<void> {
  const { user } = Store.getState();
  const allActiveFeatBitTests = await getActiveAbFeatBitFlags(
    user.sessionId ?? "",
    user.emailAddress ?? "",
  );
  // Future PBI 1380153 will need to account for CloudFlare cookies here
  const currentVariationIdFromCookie = getCurrentVariationIdFromCookie();
  let runningTestLimit = getEligibleABTestLimit();

  const cloudFlareAbTests = getCloudflareABTestCookies();
  const featBitTestsCurrentlyInVariationId = getSavedFeatBitTestInformation(
    currentVariationIdFromCookie || "",
    cloudFlareAbTests,
    allActiveFeatBitTests,
  );
  const allActiveFeatBitTestsInformation: DecodedTestInformation[] = [];
  allActiveFeatBitTests.forEach((abTest) => {
    const decoded = getDecodedTestInformation(abTest);
    if (decoded) {
      allActiveFeatBitTestsInformation.push(decoded);
    }
  });

  const expiredTestsInfo = getExpiredTestsFromCookie(
    featBitTestsCurrentlyInVariationId,
    allActiveFeatBitTestsInformation,
  );
  if (expiredTestsInfo) {
    runningTestLimit = updateStorageForExpiredTests(
      expiredTestsInfo.tests,
      expiredTestsInfo.isExclusive,
      runningTestLimit,
    );
  }

  const enrolledFeatBitTests: DecodedTestInformation[] = [];
  // Exclusive tests always have priority when enrolling a user in a new test
  const exclusiveFeatBitTest = getExclusiveTestIfEligible(
    featBitTestsCurrentlyInVariationId,
    allActiveFeatBitTestsInformation,
    runningTestLimit,
  );

  if (exclusiveFeatBitTest) {
    updateStorageForNewTests([exclusiveFeatBitTest]);
    // If an exclusive test is returned from checkAndGetExclusiveTestIfElligible and does not have 0 as their test, the user has a new exclusve test and should have their limit set to 0
    runningTestLimit = 0;
  }

  // Users in an exclusive test can ONLY be in an exclusive test
  if (exclusiveFeatBitTest) {
    enrolledFeatBitTests.push(exclusiveFeatBitTest);
  } else {
    const parsedActiveABFeatBitTests = parseActiveFeatBitTests(
      featBitTestsCurrentlyInVariationId,
      allActiveFeatBitTestsInformation,
      runningTestLimit,
    );
    updateStorageForNewTests(parsedActiveABFeatBitTests.newTests);
    runningTestLimit = parsedActiveABFeatBitTests.newLimit;
    enrolledFeatBitTests.push(...parsedActiveABFeatBitTests.allEnrolledTests);
  }

  const sortedCombinedTests = [
    ...enrolledFeatBitTests,
    ...cloudFlareAbTests,
  ].sort((a, b) => a.fullTestId.localeCompare(b.fullTestId));
  const constructed = buildVariationIdFromActiveABTests(sortedCombinedTests);

  if (
    !constructed.includes("eee:e") &&
    constructed !== currentVariationIdFromCookie
  ) {
    handleChangedVariationIdCookie(constructed);
    updateEligibleTestLimit(runningTestLimit);
  }
}
